import Autocomplete from '@mui/material/Autocomplete';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';

/**
 * An editor for one configuration value, chosen from the parameter's declared JSON Schema: a
 * number, a Yes/No choice, a choice, a list of free values, a list of choices, or a list of rules (each a
 * set of choices). Anything else is edited as JSON. The API validates the value against the same
 * declaration, so this is convenience, never the check (SDD §22.4).
 */
export type JsonSchema = Readonly<Record<string, unknown>>;

export function ValueEditor({
  schema,
  value,
  onChange,
  label,
}: {
  schema: JsonSchema;
  value: unknown;
  onChange: (value: unknown) => void;
  label: string;
}) {
  const kind = kindOf(schema);
  switch (kind.type) {
    case 'number':
      return (
        <TextField
          label={label}
          type="number"
          value={typeof value === 'number' ? String(value) : ''}
          onChange={(e) => {
            onChange(e.target.value === '' ? undefined : Number(e.target.value));
          }}
          slotProps={{
            htmlInput: {
              ...(kind.min !== undefined ? { min: kind.min } : {}),
              ...(kind.max !== undefined ? { max: kind.max } : {}),
              step: kind.integer ? 1 : 'any',
            },
          }}
          helperText={rangeHint(kind.min, kind.max)}
        />
      );
    case 'boolean':
      return (
        // A choice, not a switch: a parameter that is not set is neither Yes nor No, and a switch
        // would show it as No.
        <TextField
          select
          label={label}
          value={typeof value === 'boolean' ? (value ? 'yes' : 'no') : ''}
          onChange={(e) => {
            onChange(e.target.value === 'yes');
          }}
        >
          <MenuItem value="yes">Yes</MenuItem>
          <MenuItem value="no">No</MenuItem>
        </TextField>
      );
    case 'enum':
      return (
        <TextField
          select
          label={label}
          value={typeof value === 'string' ? value : ''}
          onChange={(e) => {
            onChange(e.target.value);
          }}
        >
          {kind.options.map((option) => (
            <MenuItem key={option} value={option}>
              {option.replace(/_/g, ' ')}
            </MenuItem>
          ))}
        </TextField>
      );
    case 'string':
      return (
        <TextField
          label={label}
          value={typeof value === 'string' ? value : ''}
          onChange={(e) => {
            onChange(e.target.value);
          }}
        />
      );
    case 'string-list':
    case 'enum-list':
      return (
        <Autocomplete
          multiple
          freeSolo={kind.type === 'string-list'}
          options={kind.type === 'enum-list' ? kind.options : []}
          value={stringArray(value)}
          onChange={(_e, next) => {
            onChange(next);
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label={label}
              helperText={
                kind.type === 'string-list' ? 'Type a value and press Enter to add it.' : undefined
              }
            />
          )}
        />
      );
    case 'rule-list':
      return (
        <RuleListEditor label={label} options={kind.options} value={value} onChange={onChange} />
      );
    default:
      return <JsonEditor label={label} value={value} onChange={onChange} />;
  }
}

function RuleListEditor({
  label,
  options,
  value,
  onChange,
}: {
  label: string;
  options: readonly string[];
  value: unknown;
  onChange: (value: unknown) => void;
}) {
  const rules = Array.isArray(value) ? value.map(stringArray) : [];
  return (
    <Stack spacing={1.5}>
      <Typography variant="body2" color="text.secondary">
        {label}: each rule is a set of attributes; a record equal to another on every attribute of
        any rule is refused.
      </Typography>
      {rules.map((rule, index) => (
        <Stack key={index} direction="row" spacing={1} sx={{ alignItems: 'center' }}>
          <Autocomplete
            multiple
            fullWidth
            options={[...options]}
            value={rule}
            onChange={(_e, next) => {
              onChange(rules.map((r, i) => (i === index ? next : r)));
            }}
            renderInput={(params) => <TextField {...params} label={`Rule ${String(index + 1)}`} />}
          />
          <IconButton
            aria-label={`Remove rule ${String(index + 1)}`}
            onClick={() => {
              onChange(rules.filter((_r, i) => i !== index));
            }}
          >
            <DeleteOutlinedIcon />
          </IconButton>
        </Stack>
      ))}
      <Button
        variant="outlined"
        sx={{ alignSelf: 'flex-start' }}
        onClick={() => {
          onChange([...rules, []]);
        }}
      >
        Add rule
      </Button>
    </Stack>
  );
}

function JsonEditor({
  label,
  value,
  onChange,
}: {
  label: string;
  value: unknown;
  onChange: (value: unknown) => void;
}) {
  const text = value === undefined ? '' : JSON.stringify(value, null, 2);
  return (
    <TextField
      label={`${label} (JSON)`}
      multiline
      minRows={4}
      defaultValue={text}
      onChange={(e) => {
        try {
          onChange(JSON.parse(e.target.value));
        } catch {
          onChange(undefined);
        }
      }}
      helperText="Enter the value as JSON."
    />
  );
}

type Kind =
  | { type: 'number'; integer: boolean; min?: number; max?: number }
  | { type: 'boolean' }
  | { type: 'enum'; options: readonly string[] }
  | { type: 'string' }
  | { type: 'string-list' }
  | { type: 'enum-list'; options: readonly string[] }
  | { type: 'rule-list'; options: readonly string[] }
  | { type: 'json' };

function enumOf(schema: unknown): readonly string[] | null {
  if (typeof schema !== 'object' || schema === null) return null;
  const values = (schema as { enum?: unknown }).enum;
  return Array.isArray(values) && values.every((v) => typeof v === 'string') ? values : null;
}

export function kindOf(schema: JsonSchema): Kind {
  const type = schema.type;
  const options = enumOf(schema);
  if (options !== null) return { type: 'enum', options };
  if (type === 'integer' || type === 'number') {
    return {
      type: 'number',
      integer: type === 'integer',
      ...(typeof schema.minimum === 'number' ? { min: schema.minimum } : {}),
      ...(typeof schema.maximum === 'number' ? { max: schema.maximum } : {}),
    };
  }
  if (type === 'boolean') return { type: 'boolean' };
  if (type === 'string') return { type: 'string' };
  if (type === 'array') {
    const items = schema.items as JsonSchema | undefined;
    if (items === undefined) return { type: 'json' };
    const itemOptions = enumOf(items);
    if (itemOptions !== null) return { type: 'enum-list', options: itemOptions };
    if (items.type === 'string') return { type: 'string-list' };
    if (items.type === 'array') {
      const inner = enumOf(items.items);
      if (inner !== null) return { type: 'rule-list', options: inner };
    }
  }
  return { type: 'json' };
}

function stringArray(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((v): v is string => typeof v === 'string') : [];
}

function rangeHint(min: number | undefined, max: number | undefined): string | undefined {
  if (min !== undefined && max !== undefined) return `From ${String(min)} to ${String(max)}.`;
  if (min !== undefined) return `At least ${String(min)}.`;
  if (max !== undefined) return `At most ${String(max)}.`;
  return undefined;
}

/** A configured value as one line of text, for tables and history. */
export function describeValue(value: unknown): string {
  if (value === undefined) return 'Not set';
  if (Array.isArray(value)) {
    if (value.length === 0) return '(none)';
    return value
      .map((item) => (Array.isArray(item) ? `[${item.map(String).join(' + ')}]` : String(item)))
      .join(', ');
  }
  if (typeof value === 'object' && value !== null) return JSON.stringify(value);
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'number') return String(value);
  return typeof value === 'string' ? value : '';
}
